<template>
  <div
    ref="popoverRef"
    class="absolute z-99"
    :style="{ left: coordinates.x + 'px', top: coordinates.y + 'px', maxHeight: maxHeight + 'px', overflowY: 'auto' }"
    @click.stop
  >
    <CCard :bordered="false" class="w-160 bg-primary6" size="small">
      <div ref="dragHandle" class="drag-handle cursor-move flex items-center justify-center">
        <CIcon icon="grip-dots" size="20px" />
      </div>

      <div v-if="isRestoring || isSelecting">
        <NSkeleton v-for="i in 5" :key="i" class="mb-2" :height="298 / 5" :sharp="false" />
      </div>

      <NForm v-else class="pa-4" @submit.prevent="onSubmit">
        <COverflowWrapper :max-height="500">
          <div class="flex flex-col gap-4 mb-2">
            <div class="font-bold font-size-[20px]">{{ t('canvasUI.components.fileViewer.linkAnnotation.title') }}</div>
            <div>
              {{ t('canvasUI.components.fileViewer.linkAnnotation.subtitle') }}
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <NFormItem :show-feedback="false" :show-label="false">
              <CSelect v-model:value="linkType" bordered :options="linkTypeOptions" :popover-props="{ zIndex: 100 }" />
            </NFormItem>

            <template v-if="linkType === 'web'">
              <div class="flex flex-col gap-2">
                <label>{{ t('canvasUI.components.fileViewer.linkAnnotation.urlInput') }}</label>
                <CInput v-model:value="urlInput" placeholder="https://www.example.com" type="text" />
              </div>
            </template>

            <template v-if="linkType === 'file' && isVideo && file?.content_url">
              <CCard content-class="flex gap-1 justify-center w-full">
                <div class="font-bold">{{ t('canvasUI.components.fileViewer.linkAnnotation.videoStartTime') }}</div>
                <div>{{ formattedVideoStartTime }}</div>
              </CCard>

              <CVideo
                class="max-h-[200px] overflow-auto"
                :options="{
                  maxHeight: 200,
                  sources: [
                    {
                      src: file?.content_url,
                    },
                  ],
                }"
                @pause="handleVideoStop"
                @seeked="handleVideoStop"
              />
            </template>

            <CCard v-if="linkType === 'file'" content-class="flex justify-center w-full">
              <CSingleFileSelector
                :file="file"
                :is-selecting="false"
                :selected-page-index="selectedPageIndex"
                @clearSelectedFile="clearSelectedFile"
                @openContentSelector="openContentSelector"
              />
            </CCard>
            <div class="flex gap-4">
              <CCheckbox
                v-model:checked="newTab"
                :label="t('canvasUI.components.fileViewer.linkAnnotation.openInNewTab')"
              />
              <CCheckbox
                v-if="linkType === 'file' && isVideo && launchDarkly.enable_embedded_video"
                v-model:checked="embedded"
                :label="t('canvasUI.components.fileViewer.linkAnnotation.embedInPage')"
              />
              <CCheckbox
                v-if="linkType === 'file' && isVideo && launchDarkly.enable_embedded_video_autoplay"
                v-model:checked="autoplay"
                :disabled="!embedded"
                :label="t('canvasUI.components.fileViewer.linkAnnotation.embedAutoplay')"
              />
            </div>
            <div class="flex flex-col gap-2 mt-2">
              <div class="flex flex-col gap-2">
                <label> {{ t('canvasUI.components.fileViewer.linkAnnotation.trackingId') }}</label>
                <CInput v-model:value="trackingId" />
              </div>
              <div class="flex flex-col gap-2">
                <label> {{ t('canvasUI.components.fileViewer.linkAnnotation.tags') }}</label>
                <CTags v-model:value="tags" />
              </div>
            </div>
          </div>
        </COverflowWrapper>
        <div class="flex justify-between mt-8">
          <CButton :disabled="isSelecting" @click="onClose">
            {{ t('canvasUI.common.cancel') }}
          </CButton>
          <CButton
            attr-type="submit"
            :disabled="isSelecting || (linkType === 'file' && !file) || (linkType === 'web' && !isValidUrl)"
            type="primary"
          >
            {{ t('canvasUI.components.fileViewer.linkAnnotation.setLink') }}
          </CButton>
        </div>
      </NForm>
    </CCard>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, inject, toRaw } from 'vue'
import type { ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDraggable, useLocalStorage } from '@vueuse/core'
import { NForm, NFormItem, NSkeleton } from 'naive-ui'
import type { AnnotationsUnion } from 'pspdfkit'

import CCheckbox from '@lib/components/CCheckbox/CCheckbox.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import COverflowWrapper from '@lib/components/COverflowWrapper/COverflowWrapper.vue'
import CSingleFileSelector from '@lib/components/CSingleFileSelector/CSingleFileSelector.vue'
import CVideo from '@lib/components/CVideo/CVideo.vue'
import CTags from '@lib/components/CTags/CTags.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import { secondsToHumanReadable } from '@lib/util/date'
import type { FileRetrieve } from '@openapi'
import { FileContentTypeEnum } from '@openapi'
import type { SaveLinkAnnotationPayload } from '@lib/components/CFileViewer/pdf/CFileViewer.pdf.link.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { isValidUrl as validateUrl, enforceHttps } from '@canvas-builder/util/url'

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const props = defineProps<{
  annotation: AnnotationsUnion | null
  selectContent?: (
    fileId?: FileRetrieve['id'],
    pageIndex?: number,
  ) => Promise<(FileRetrieve & { pageIndex?: number }) | null>
  fetchFileById?: (id: FileRetrieve['id']) => Promise<FileRetrieve>
}>()

const emit = defineEmits<{
  save: [data: SaveLinkAnnotationPayload]
  close: []
  openContentSelector: []
}>()

const { t } = useI18n()

const allLinkTypeOptions = [
  { label: t('canvasUI.components.fileViewer.linkAnnotation.file'), value: 'file' },
  { label: t('canvasUI.components.fileViewer.linkAnnotation.webPage'), value: 'web' },
] as const

const linkTypeOptions = computed(() => {
  if (launchDarkly.value?.enable_url_hotspots) {
    return allLinkTypeOptions
  }
  return [allLinkTypeOptions[0]]
})

const linkType = ref<(typeof allLinkTypeOptions)[number]['value']>('file')
const file = ref<FileRetrieve | null>(null)
const popoverRef = ref<HTMLElement | null>(null)
const dragHandle = ref<HTMLElement | null>(null)
const selectedPageIndex = ref(0)
const newTab = ref(true)
const embedded = ref(launchDarkly.value.enable_embedded_video)
const autoplay = ref(launchDarkly.value.enable_embedded_video_autoplay)
const videoCurrentTime = ref(0)
const tags = ref<string[]>([])
const trackingId = ref<string>('')
const urlInput = ref<string>('')
const isSelecting = ref(false)
const isRestoring = ref(false)
const maxHeight = ref(calculateMaxHeight())

const formattedVideoStartTime = computed(() => {
  return secondsToHumanReadable(videoCurrentTime.value)
})

const isVideo = computed(() => file.value?.content_type === FileContentTypeEnum.VIDEO)

const isValidUrl = computed(() => {
  if (!urlInput.value) return false
  return validateUrl(urlInput.value)
})

const storageKey = computed(() => 'hotspot-editor-coords')

const coordinates = useLocalStorage(storageKey.value, { x: 0, y: 0 })

function calculateMaxHeight() {
  const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)
  return vh - 150
}

function handleResize() {
  maxHeight.value = calculateMaxHeight()
}

const updatePositionWithinBounds = (x: number, y: number) => {
  if (popoverRef.value) {
    // Get the viewport dimensions
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)

    // Get the dimensions of the draggable element
    const elementWidth = popoverRef.value.offsetWidth
    const elementHeight = popoverRef.value.offsetHeight

    // Calculate the boundaries
    const minX = 0
    const minY = 0
    const maxX = vw - elementWidth
    const maxY = vh - elementHeight

    // Ensure the new position is within the boundaries
    const clampedX = Math.max(minX, Math.min(x, maxX))
    const clampedY = Math.max(minY, Math.min(y, maxY))

    return { x: clampedX, y: clampedY }
  }
  return { x, y }
}

useDraggable(popoverRef, {
  handle: dragHandle,
  initialValue: coordinates.value,
  onMove: (position) => {
    const { x: newX, y: newY } = updatePositionWithinBounds(position.x, position.y)
    coordinates.value = { x: newX, y: newY }
  },
})

const onClose = () => {
  emit('close')
}

const onSubmit = () => {
  if (linkType.value === 'file') {
    const data: SaveLinkAnnotationPayload = {
      customData: {
        type: 'file',
        fileId: file.value!.id,
        pageIndex: selectedPageIndex.value,
        newTab: newTab.value,
        tags: toRaw(tags.value),
        trackingId: trackingId.value,
        ...(isVideo.value
          ? {
              startTime: videoCurrentTime.value,
              embedded: embedded.value,
              autoplay: embedded.value ? autoplay.value : false,
            }
          : {}),
      },
    }
    emit('save', data)
  } else if (linkType.value === 'web') {
    const data: SaveLinkAnnotationPayload = {
      customData: {
        type: 'url',
        url: enforceHttps(urlInput.value),
        newTab: newTab.value,
        tags: toRaw(tags.value),
        trackingId: trackingId.value,
      },
    }
    emit('save', data)
  }
}

const clearSelectedFile = () => {
  file.value = null
  selectedPageIndex.value = 0
  videoCurrentTime.value = 0
  embedded.value = false
  autoplay.value = false
  urlInput.value = ''
  tags.value = []
  trackingId.value = ''
}

const openContentSelector = () => {
  if (!props.selectContent) {
    console.warn('selectContent function is not provided')
    return
  }
  isSelecting.value = true
  props
    .selectContent(file.value?.id, selectedPageIndex.value)
    .then((response) => {
      if (!response) return
      const { pageIndex, ...fileDetails } = response

      if (file.value?.id !== fileDetails.id) videoCurrentTime.value = 0

      file.value = fileDetails as FileRetrieve
      selectedPageIndex.value = pageIndex ?? 0
    })
    .finally(() => {
      isSelecting.value = false
    })
}

const positionPopover = () => {
  if (popoverRef.value) {
    const pdfContainer = document.querySelector('.PSPDFKit-Container') as HTMLElement
    if (pdfContainer) {
      const containerRect = pdfContainer.getBoundingClientRect()
      coordinates.value.x = coordinates.value.x || containerRect.right - popoverRef.value.offsetWidth - 10
      coordinates.value.y = coordinates.value.y || containerRect.top + 50
    }
  }
}

function handleVideoStop(currentTime: number) {
  videoCurrentTime.value = currentTime
}

onMounted(async () => {
  window.addEventListener('resize', handleResize)
  handleResize()
  window.addEventListener('resize', positionPopover)
  positionPopover()

  if (props.annotation?.customData) {
    try {
      isRestoring.value = true

      if (props.annotation.customData.type === 'file' && props.fetchFileById) {
        const fileId = props.annotation.customData.fileId
        if (fileId) {
          linkType.value = 'file'
          file.value = await props.fetchFileById(fileId as FileRetrieve['id'])
          selectedPageIndex.value = (props.annotation.customData.pageIndex ?? 0) as number
          videoCurrentTime.value = (props.annotation.customData.startTime ?? 0) as number
          embedded.value = (props.annotation.customData.embedded ?? false) as boolean
          autoplay.value = (props.annotation.customData.autoplay ?? false) as boolean
        }
      } else if (props.annotation.customData.type === 'url') {
        if (launchDarkly.value?.enable_url_hotspots) {
          linkType.value = 'web'
          urlInput.value = (props.annotation.customData.url ?? '') as string
        } else {
          linkType.value = 'file'
        }
      }

      // Restore common fields
      newTab.value = (props.annotation.customData.newTab ?? false) as boolean
      tags.value = (props.annotation.customData.tags ?? []) as string[]
      trackingId.value = (props.annotation.customData.trackingId ?? '') as string
    } finally {
      isRestoring.value = false
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('resize', positionPopover)
})
</script>
